import React, { useEffect, useState } from 'react';

import { Image } from '@codes/ui-kit/components/ui/image';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Dialog, DialogContent, DialogTitle } from '@codes/ui-kit';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface CatalogImageSliderProps {
    images: string[];
    name: string;
    stereo: string;
    iconImage: string;
}

const ImageSwiper: React.FC<{
    images: string[];
    name: string;
    stereo: string;
    onImageClick?: (image: string, index: number) => void;
    isModal?: boolean;
    initialSlide?: number;
    iconImage: string;
}> = ({ images, name, stereo, iconImage, onImageClick, isModal = false, initialSlide = 0 }) => {
    const [isZoomed, setIsZoomed] = useState(false);
    const [swiper, setSwiper] = useState(null);

    useEffect(() => {
        if (isModal && swiper) {
            swiper.slideTo(initialSlide, 0);
        }
    }, [isModal, initialSlide]);

    const handleDoubleClick = () => {
        if (isModal) {
            setIsZoomed(!isZoomed);
        }
    };

    return (
        <Swiper
            className="h-full"
            modules={[Navigation, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            loop={true}
            pagination={{
                clickable: true,
            }}
            onSwiper={swiper => setSwiper(swiper)}
            initialSlide={initialSlide}
        >
            {images && images.length > 0 ? (
                images.map((image, index) => (
                    <SwiperSlide key={index}>
                        <div
                            className={`w-full h-full flex items-center justify-center ${
                                isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
                            }`}
                            onDoubleClick={handleDoubleClick}
                        >
                            <Image
                                src={image}
                                alt={`${name} - Image ${index + 1}`}
                                className={`w-full h-full ${isZoomed ? 'object-cover scale-150' : ''} ${
                                    isModal ? 'object-contain' : 'object-cover'
                                } transition-transform duration-300`}
                                onClick={() => onImageClick && onImageClick(image, index)}
                            />
                        </div>
                    </SwiperSlide>
                ))
            ) : (
                <SwiperSlide>
                    <div className="w-full h-full flex items-center justify-center">
                        <Image src={iconImage} alt={stereo} />
                    </div>
                </SwiperSlide>
            )}
        </Swiper>
    );
};

export const CatalogImageSlider: React.FC<CatalogImageSliderProps> = ({ images, name, stereo, iconImage }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const openModal = (image: string, index: number) => {
        console.log(index);
        setSelectedImageIndex(index);
        setIsModalOpen(true);
    };

    return (
        <>
            <div className="bg-[#F7F8F9] dark:bg-[#252525] w-full h-full aspect-[452/304] border border-[#DFE0E2] dark:border-[#53555B] rounded-lg overflow-hidden">
                <ImageSwiper
                    images={images}
                    name={name}
                    stereo={stereo}
                    iconImage={iconImage}
                    onImageClick={openModal}
                />
            </div>
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogTitle />
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0" aria-describedby={undefined}>
                    <div className="bg-box w-full h-full aspect-[1] overflow-hidden">
                        <ImageSwiper
                            images={images}
                            name={name}
                            stereo={stereo}
                            iconImage={iconImage}
                            isModal={true}
                            initialSlide={selectedImageIndex}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
